import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Chip } from "@mui/material";

import MonthlyPaymentEmployeeBreakdown from "./MonthlyPaymentEmployeeBreakdown";
import OneTimePaymentBreakdown from "./OneTimePaymentBreakdown";
import Button from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import CustomChip from "components/common/Chip";
import { AlertType } from "components/common/Snackbar";

import { handleRetryPayment, currencyFormat, getValue } from "components/Screens/CommonUtils";
import { usePermissions } from "components/Hook";
import { getLoadingAdditionalInfo } from "selectors/user";

import { getCustomerInvoice, downloadCustomerInvoicePDF } from "services/customer";

import { RetryPayment } from "store/actions/Payment/RetryPayment";
import { showSnackbar } from "store/actions/Utility";

import { getRetryPayment } from "selectors/customer";

import {
  getBackgroundImage,
  getChipColor,
  getChipLabel,
  getMonthlyPaymentOverviewDetails,
  getOneTimePaymentOverviewDetails,
  InvoiceStatusMap,
} from "./utils";

import { CUSTOMER_PAYMENT_ROUTE } from "constants/routes";

import { FileDownloadOutlined as DownloadIcon } from "@mui/icons-material";

import "./PayrollDetails.scss";

function PayrollDetails({ getPayrollMonth }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loggedInUser } = usePermissions();
  const { state: locationState } = useLocation();
  const dispatch = useDispatch();

  const [payrollBreakdown, setPayrollBreakdown] = useState({});
  const [loadingPayrollBreakdown, setLoadingPayrollBreakdown] = useState(true);

  const loadingAdditionalInfo = useSelector(getLoadingAdditionalInfo);
  const retryPaymentDetails = useSelector(getRetryPayment);

  const { paymentType: paymentTypeFromLocation, customerId: customerIdFromLocation } = locationState || {};
  const paymentTypeFromParam = paymentTypeFromLocation?.toLowerCase() || "monthly";

  const handleGetPayrollBreakdown = useCallback(async () => {
    try {
      setLoadingPayrollBreakdown(true);
      const response = await getCustomerInvoice(id, paymentTypeFromParam, dispatch, customerIdFromLocation);

      const {
        paymentStatus,
        paymentType,
        initialSalaries = 0,
        totalRaise = 0,
        totalBonus = 0,
        totalDiscount = 0,
        creditAdjustment = 0,
        totalPayrollAmount,
        billingPeriod,
      } = response || {};

      const totalPayroll =
        totalPayrollAmount || initialSalaries + totalRaise + totalBonus + totalDiscount + creditAdjustment;

      setPayrollBreakdown({
        ...response,
        status: InvoiceStatusMap[paymentStatus] || "pending",
        customerPayrollId: id,
        failure: paymentStatus === "Failed",
        totalIncrement: totalRaise,
        paymentType: paymentType?.toLowerCase() || "monthly",
        totalPayroll,
      });
      // for setting payroll month in header
      getPayrollMonth(billingPeriod);
    } catch (error) {
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: "Failed to fetch invoice data. Please try again.",
          dispatch,
        })
      );
    } finally {
      setLoadingPayrollBreakdown(false);
    }
  }, [id, paymentTypeFromParam, dispatch, getPayrollMonth]);

  useEffect(() => {
    handleGetPayrollBreakdown();
  }, []);

  useEffect(() => {
    const { loadingRetryPayment, retryPayment } = retryPaymentDetails;
    if (!loadingRetryPayment && retryPayment?.status === "payment in progress") {
      handleGetPayrollBreakdown();
    }
  }, [handleGetPayrollBreakdown, retryPaymentDetails]);

  const handleDownloadReceipt = async (customerPayrollId) => {
    await downloadCustomerInvoicePDF(customerPayrollId, paymentTypeFromParam, dispatch, customerIdFromLocation);
  };

  const {
    billingPeriod,
    status,
    totalPayroll,
    failure,
    customerPayrollId,
    paymentType,
    employeePayrolls = [],
    invoiceItems = [],
    totalAmount,
  } = payrollBreakdown || {};
  const totalInvoiceAmount = paymentType === "monthly" ? totalPayroll : totalAmount;

  const oneTimePaymentOverview = () => {
    if (!payrollBreakdown || Object.keys(payrollBreakdown).length === 0) {
      return null;
    }

    return (
      <div className="payroll-details-payment-overview-section">
        <p className="payroll-details-payment-overview-heading">Payment overview</p>

        <div className="payroll-details-payment-overview-details">
          <p>Payment status</p>
          <CustomChip
            sx={{
              "&.MuiChip-root": {
                borderRadius: "0px",
              },
            }}
            size="small"
            color={getChipColor(payrollBreakdown)}
            label={getChipLabel(payrollBreakdown)}
          />
        </div>

        <div className="payroll-details-payment-overview-details">
          <p>Billing period</p>
          <div className="payment-detail-value">{billingPeriod}</div>
        </div>

        {getOneTimePaymentOverviewDetails(payrollBreakdown).map(({ label, value }) => (
          <div className="payroll-details-payment-overview-details" key={label}>
            <p>{label}</p>
            <div className="payment-detail-value">{value}</div>
          </div>
        ))}
      </div>
    );
  };

  const monthlyPaymentOverview = () => {
    if (!payrollBreakdown || Object.keys(payrollBreakdown).length === 0) {
      return null;
    }

    return (
      <div className="payroll-details-payment-overview-section">
        <p className="payroll-details-payment-overview-heading">Payment overview</p>

        <div className="payroll-details-payment-overview-details">
          <p>Payment status</p>
          <CustomChip
            sx={{
              "&.MuiChip-root": {
                borderRadius: "0px",
              },
            }}
            size="small"
            color={getChipColor(payrollBreakdown)}
            label={getChipLabel(payrollBreakdown)}
          />
        </div>

        <div className="payroll-details-payment-overview-details">
          <p>Billing period</p>
          <div className="payment-detail-value">{billingPeriod}</div>
        </div>

        {getMonthlyPaymentOverviewDetails(payrollBreakdown).map(({ label, value }) => (
          <div className="payroll-details-payment-overview-details" key={label}>
            <p>{label}</p>
            <div className="payment-detail-value">{value}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {(loadingAdditionalInfo || loadingPayrollBreakdown) && <PageLoader />}
      <div className="payroll-details-main-container">
        <div className="payroll-details-header-section">
          <div
            className="payroll-details-main-header"
            style={{
              background: getBackgroundImage(payrollBreakdown),
            }}
          >
            <div className="payroll-details-header-left">
              <Chip
                sx={{
                  background: "#FCFCFF",
                  marginBottom: "12px",
                }}
                label={billingPeriod}
              />

              <div
                className={`para-body-m-medium color-text-heading-primary ${
                  status === "pending" ? "" : "payroll-details-header-white-text"
                }`}
              >
                Total amount
              </div>
              <div
                className={`heading-h1-semibold color-text-heading-primary ${
                  status === "pending" ? "" : "payroll-details-header-white-text"
                }`}
              >
                ${currencyFormat(getValue(totalInvoiceAmount))}
              </div>
            </div>
            {failure ? (
              <div className="payroll-details-failure-btns-container">
                <Button
                  variant="outlined"
                  size="lg"
                  id="retry-payment"
                  label="Retry Payment"
                  onClick={() =>
                    handleRetryPayment(loggedInUser?.customerId, customerPayrollId, dispatch, RetryPayment)
                  }
                />
                <Button
                  variant="outlined"
                  size="lg"
                  id="change-payment-method"
                  label="Change Payment Method"
                  onClick={() => navigate(CUSTOMER_PAYMENT_ROUTE)}
                />
              </div>
            ) : (
              status === "paid" && (
                <div className="payroll-details-download-invoice-btn-container">
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    size="lg"
                    id="download-invoice"
                    label="Download invoice"
                    onClick={() => handleDownloadReceipt(customerPayrollId)}
                  />
                </div>
              )
            )}
          </div>
        </div>

        {paymentType === "monthly" ? monthlyPaymentOverview() : oneTimePaymentOverview()}

        <div className="payroll-details-payment-breakdown-section">
          {paymentType === "monthly" ? (
            <MonthlyPaymentEmployeeBreakdown employeesBreakdown={employeePayrolls} />
          ) : (
            <OneTimePaymentBreakdown employeesBreakdown={invoiceItems} />
          )}
        </div>
      </div>
    </>
  );
}

export default PayrollDetails;
